import { useCallback, useEffect, useRef, useState } from "react";
import type { Exercise } from "../core/exercises";
import type { Backend } from "../core/storage";
import { advance, clearActiveWorkout, dueCues, loadActiveWorkout, saveActiveWorkout, type Workout } from "../core/workout";
import { platform } from "../platform";
import { playCues, unlockAudio } from "./cues";

const TICK_MS = 200;

/**
 * The workout in progress, held by the App so it keeps running on every tab. Loads a saved workout on start, saves
 * every change, and while a workout runs ticks a few times per second: `advance` performs what became due and the
 * cues are played. Keeps the screen on meanwhile. `asleepMs`: how long the app was asleep (hidden page, frozen app)
 * while cues came due that were not played; the screen says so until `dismissAsleep`.
 */
export function useWorkout(db: Backend | undefined, exercises: Exercise[]) {
  const [workout, setWorkout] = useState<Workout>();
  const [now, setNow] = useState(() => Date.now());
  const current = useRef<Workout>(undefined);
  const glossary = useRef(exercises);
  glossary.current = exercises;
  const lastTick = useRef(Date.now());
  const [asleepMs, setAsleepMs] = useState(0);

  const commit = useCallback((next: Workout | undefined) => {
    if (next === current.current) return;
    current.current = next;
    setWorkout(next);
    if (db) (next ? saveActiveWorkout(db, next) : clearActiveWorkout(db)).catch((e) => console.error("saving the workout failed", e));
  }, [db]);

  /** Changes the workout (with the current time); every change is saved. */
  const apply = useCallback((fn: (w: Workout, now: number) => Workout) => {
    unlockAudio();
    const w = current.current;
    if (w) commit(fn(w, Date.now()));
  }, [commit]);

  useEffect(() => {
    if (!db) return;
    loadActiveWorkout(db).then((w) => {
      if (w && !current.current) { current.current = w; setWorkout(w); }
    }).catch((e) => console.error("loading the workout failed", e));
  }, [db]);

  const running = !!workout;
  useEffect(() => {
    if (!running) return;
    lastTick.current = Date.now();
    const id = setInterval(() => {
      const t = Date.now();
      const w = current.current;
      if (!w) return;
      const r = advance(w, glossary.current, t, lastTick.current);
      const due = dueCues(r.cues, t);
      const gap = t - lastTick.current;
      if (due.missed.length) setAsleepMs((ms) => ms + gap);
      lastTick.current = t;
      commit(r.workout);
      playCues(due.play);
      setNow(t);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [running, commit]);

  useWakeLock(running);

  return {
    workout, now, apply, asleepMs,
    dismissAsleep: () => setAsleepMs(0),
    start: (w: Workout) => { unlockAudio(); commit(w); },
    end: () => { setAsleepMs(0); commit(undefined); },
  };
}

/** Keeps the screen on while `on`. */
export function useWakeLock(on: boolean) {
  useEffect(() => (on ? platform.keepAwake() : undefined), [on]);
}
